import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Waves, Envelope, Spinner, CheckCircle, ArrowLeft } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { API_URL } from '../utils/api';
import { Logo, LogoText } from '../components/Logo';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Enter your email'); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--bg-main)', color: 'var(--text-primary)' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <Logo size="lg" />
            <span className="font-black text-2xl tracking-tight">index24hrs<span style={{ color: 'var(--accent)' }}>.com</span></span>
          </Link>
        </div>

        <div className="border rounded-xl p-8" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)' }}>
          {sent ? (
            <div className="text-center">
              <CheckCircle weight="fill" className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">Check Your Email</h2>
              <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
                If an account with <strong>{email}</strong> exists, we've sent a password reset link.
              </p>
              <p className="text-xs mb-6" style={{ color: 'var(--text-muted)' }}>The link expires in 1 hour.</p>
              <Link to="/login" className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="back-to-login-btn">
                <ArrowLeft className="w-4 h-4" /> Back to Login
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'var(--accent-bg)', border: '1px solid var(--border-accent)' }}>
                  <Envelope weight="duotone" className="w-7 h-7" style={{ color: 'var(--accent)' }} />
                </div>
                <h2 className="text-xl font-bold">Forgot Password?</h2>
                <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Enter your email and we'll send you a reset link</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm mb-1.5" style={{ color: 'var(--text-secondary)' }}>Email Address</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="input-field" data-testid="forgot-email-input" />
                </div>
                <button type="submit" disabled={loading} className="w-full btn-primary flex items-center justify-center gap-2 py-3" data-testid="send-reset-link">
                  {loading ? <Spinner className="w-5 h-5 animate-spin" /> : <><Envelope weight="bold" className="w-5 h-5" />Send Reset Link</>}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-sm mt-6" style={{ color: 'var(--text-muted)' }}>
          Remember your password?{' '}
          <Link to="/login" className="font-medium" style={{ color: 'var(--accent)' }}>Sign in</Link>
        </p>
      </div>
    </div>
  );
}
